import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  useCurrentSearchId,
  useGetSearchChartData,
  useGetSelectedTargetCharts,
  useSearchFilters,
  useSearchTargetFields,
  useSelectChartData,
  useSelectedChartId,
} from 'redux/hooks/searchnFilter';
import * as echarts from 'echarts';
import SelectInput, { IOption } from 'components/input/selectInput';
import { LoadingModal } from 'components/modal';
import {
  addSearchFilterValue,
  addSearchFilters,
  removeSearchFilterValue,
  setSelectedChartId,
} from 'redux/reducers/searchnFilterReducer';
import { IFilterDTO } from 'business/models/Models';
import { useRMDispatch } from 'redux/hooks';
import { getNewId } from 'utils/Global';
import { getFilterValueDTO } from 'components/sidePanel/searchAndFilter/utils';
import { Legends } from './legends';
import {
  getFormattedSeriesData,
  getLabelsConfig,
  isNumericDataChart,
  splitOnFirstDot,
} from '../utils';
import { useGetChartOptionsData } from './hooks';
import { NULL_VALUE_TITLE, chartConfigs } from './constants';
import RMSlider from 'components/customComponents/Controls/RMSlider';
import { useHistory } from 'react-router';
import RMCheckBox from 'components/customComponents/Controls/RMCheckbox';

interface Props {
  chartInstance: any;
}

let showAxisTip = false;
const Analytics = (props: Props) => {
  const { chartInstance } = props;
  const getChartData = useGetSearchChartData();
  const chartsData = useSelectChartData() || [];
  const chartRef = useRef(null);
  const availableCharts = useGetSelectedTargetCharts();
  const filters = useSearchFilters();
  const selectedChartId = useSelectedChartId();
  const selectedChart = chartsData?.find((c) => c.chartId === selectedChartId);
  const searchTargetFields = useSearchTargetFields();
  const searchId = useCurrentSearchId();
  const dispatch = useRMDispatch();
  const latestStateRef = useRef({
    filters,
    searchTargetFields,
    availableCharts,
    selectedChartId,
    selectedChart,
  });
  const [binSize, setBinSize] = useState(1);
  const [showLabels, setShowLabels] = useState(false);
  const showLabelsRef = useRef(showLabels);
  const history = useHistory();

  const isNumericChart = useMemo(
    () => isNumericDataChart(availableCharts, selectedChartId, searchTargetFields),
    [searchTargetFields, availableCharts, selectedChartId],
  );

  const { seriesData, categories, xValues, colorsMap } = useGetChartOptionsData({
    selectedChart,
    binSize,
    isNumericChart,
  });
  const chartOptions = availableCharts?.map(
    (chart) => ({ value: chart.id, title: chart.title } as IOption),
  );

  useEffect(() => {
    if (availableCharts?.length > 0) {
      if (!availableCharts.find((c) => c.id === selectedChartId)) {
        dispatch(setSelectedChartId(availableCharts[0].id));
      }
    } else {
      const pathParts = window.location.pathname.split('/');
      pathParts.pop();
      history.push(pathParts.join('/'));
    }
  }, [availableCharts, selectedChartId]);

  useEffect(() => {
    latestStateRef.current = {
      filters,
      searchTargetFields,
      availableCharts,
      selectedChartId,
      selectedChart,
    };
  }, [filters, searchTargetFields, availableCharts, selectedChartId, selectedChart]);

  useEffect(() => {
    chartInstance.current = echarts.init(chartRef.current);
    const currentChart = availableCharts?.find((c) => c.id === selectedChartId);
    const width = chartInstance.current.getWidth();
    const { interval, rotationAngle } = getLabelsConfig(width, xValues.length);

    chartInstance.current.setOption({
      ...chartConfigs,
      title: { ...chartConfigs?.title, text: currentChart?.title },
      series: getFormattedSeriesData(seriesData, showLabelsRef.current),
      xAxis: {
        ...chartConfigs.xAxis,
        data: xValues.map((x) => (x === null ? NULL_VALUE_TITLE : x)),
        name: currentChart?.xaxis,
        axisLabel: {
          rotate: rotationAngle,
          interval,
        },
      },
      yAxis: {
        ...chartConfigs.yAxis,
        name: currentChart?.yaxis,
      },
    });
    const handleResize = () => {
      chartInstance.current.resize();
    };
    window.addEventListener('resize', handleResize);
    chartInstance.current.on('click', onBarClick);
    chartInstance.current.getZr().on('mousemove', handleShowTooltip);
    chartInstance.current.on('dataZoom', onZoomChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current.dispose();
    };
  }, [seriesData, xValues, availableCharts, selectedChartId]);

  useEffect(() => {
    if (selectedChartId && !chartsData?.find((c) => c.chartId === selectedChartId)) {
      getChartData(selectedChartId);
    }
  }, [selectedChartId]);

  const onChartSelectionChange = (e) => {
    dispatch(setSelectedChartId(parseInt(e.target.value)));
    setBinSize(1);
  };

  const onBarClick = (params) => {
    const {
      filters,
      searchTargetFields,
      availableCharts,
      selectedChartId,
      selectedChart,
    } = latestStateRef.current;

    if (params.componentType === 'series') {
      const chart = availableCharts.find((c) => c.id === selectedChartId);
      const [tableName, colName] = splitOnFirstDot(chart.xaxis);
      const searchField = searchTargetFields.find((t) => t.name === tableName);
      const searchFieldId = searchField?.searchFields?.find(
        (f) => f.displayName === colName,
      )?.id;

      const originalXValues = [
        ...new Set(selectedChart.data.map((item) => item[Object.keys(item)[0]])),
      ];
      const filter = filters.data.find((f) => f.searchFieldId === searchFieldId);

      if (!filter) {
        const filterId = getNewId();
        let filterValues = [getFilterValueDTO(filterId, params.name)];

        if (isNumericChart && binSize > 1) {
          const [min, max] = params.name.split('-');
          filterValues = originalXValues
            .filter((val) => val >= parseFloat(min) && val <= parseFloat(max))
            .map((val) => getFilterValueDTO(filterId, val.toString()));
        }

        dispatch(
          addSearchFilters([
            {
              id: filterId,
              searchFieldId,
              searchId,
              allValues: originalXValues.map((val) => val?.toString()),
              selectedValues: originalXValues.map((val) => val?.toString()),
              values: filterValues,
            } as IFilterDTO,
          ]),
        );
      }

      const clickedIndex = params.dataIndex;
      chartInstance.current?.setOption({
        dataZoom: [
          {
            startValue: clickedIndex,
            endValue: clickedIndex,
          },
        ],
      });
    }
  };

  const onLegendSelectChanged = (name: string) => {
    const chart = availableCharts.find((c) => c.id === selectedChartId);
    const [tableName, colName] = splitOnFirstDot(chart.series);
    const searchField = searchTargetFields.find((t) => t.name === tableName);
    const searchFieldId = searchField?.searchFields?.find(
      (f) => f.displayName === colName,
    )?.id;

    const filter = filters.data.find((f) => f.searchFieldId === searchFieldId);
    const allValues = categories.map((c) => (c === NULL_VALUE_TITLE ? null : c));
    if (!filter) {
      const filterId = getNewId();
      dispatch(
        addSearchFilters([
          {
            id: filterId,
            searchFieldId: searchFieldId,
            searchId: searchId,
            allValues: allValues,
            selectedValues: allValues,
            values: allValues.map((c) => getFilterValueDTO(filterId, c)),
          } as IFilterDTO,
        ]),
      );
      const newValue = getFilterValueDTO(filterId, name);
      dispatch(removeSearchFilterValue(newValue));
    } else {
      const newValue = getFilterValueDTO(filter.id, name);
      const valueIndex = filter.values.findIndex((v) => v.value === name);
      if (valueIndex !== -1) {
        dispatch(removeSearchFilterValue(newValue));
      } else {
        dispatch(addSearchFilterValue(newValue));
      }
    }
  };

  const handleShowTooltip = (event) => {
    const point = [event.offsetX, event.offsetY];
    const xDataCoord = chartInstance.current.convertFromPixel({ seriesIndex: 0 }, point);
    if (!xDataCoord) {
      return;
    }
    const dataIndex = Math.round(xDataCoord[0]);

    const chartHeight = chartInstance.current.getHeight();
    const xAxisStart = chartHeight - chartHeight * 0.18;
    const isNearXAxis = event.offsetY >= xAxisStart && event.offsetY <= xAxisStart + 25;

    if (isNearXAxis && dataIndex >= 0 && dataIndex < xValues.length) {
      if (!showAxisTip) {
        chartInstance.current.setOption({ tooltip: { trigger: 'axis' } });
        showAxisTip = true;
      }
      chartInstance.current.dispatchAction({
        type: 'showTip',
        seriesIndex: 0,
        dataIndex: dataIndex,
        position: (point, params, dom, rect, size) => {
          const mouseX = event.offsetX;
          const mouseY = event.offsetY;
          const tooltipSize = size.contentSize; // [width, height]

          return [mouseX - tooltipSize[0] / 2, mouseY - tooltipSize[1]]; // bottom-left of tooltip on mouse
        },
      });
    } else if (showAxisTip) {
      chartInstance.current.setOption({ tooltip: { trigger: 'item' } });
      chartInstance.current.dispatchAction({ type: 'hideTip' });
      showAxisTip = false;
    }
  };

  const handleShowLabelsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowLabels(e.target.checked);
    chartInstance.current.setOption({
      series: getFormattedSeriesData(seriesData, e.target.checked),
    });
    showLabelsRef.current = e.target.checked;
  };

  const onZoomChange = (params) => {
    const startPct = params.batch ? params.batch[0].start : params.start;
    const endPct = params.batch ? params.batch[0].end : params.end;

    const startIndex = Math.round((startPct / 100) * (xValues.length - 1));
    const endIndex = Math.round((endPct / 100) * (xValues.length - 1));
    const width = chartInstance.current.getWidth();

    const { interval, rotationAngle } = getLabelsConfig(width, endIndex - startIndex);
    chartInstance.current?.setOption({
      xAxis: { axisLabel: { interval, rotate: rotationAngle } },
    });
  };

  return (
    <div
      id="index-searchandfilter-analytics-wrapper"
      className="h-90 w-full flex flex-col"
    >
      <LoadingModal
        showModal={!selectedChart || selectedChart.isFetching}
        text="Loading"
      />
      <div
        id="index-searchandfilter-analytics-charts-section"
        className="basis-[10%] p-2 gap-5 flex flex-row items-center border-solid border-0 border-b border-b-gray-300"
      >
        <div
          id="index-searchandfilter-analytics-series"
          className="flex flex-row gap-3 items-center"
        >
          <label id="index-searchandfilter-analytics-series-text">Series</label>
          <div
            id="index-searchandfilter-analytics-series-charts"
            className="text-sm text-[#414141]"
          >
            {availableCharts?.find((c) => c.id === selectedChartId)?.series}
          </div>
        </div>
        <div
          id="index-searchandfilter-analytics-category"
          className="flex flex-row gap-3"
        >
          <label id="index-searchandfilter-analytics-category-text">Category</label>
          <SelectInput
            className="h-6"
            options={chartOptions}
            onChange={onChartSelectionChange}
            value={selectedChartId}
          />
        </div>
        {isNumericChart && (
          <div
            id="index-searchandfilter-analytics-binning"
            className="flex flex-row items-center basis-[30%]"
          >
            <label id="index-searchandfilter-analytics-binning-text">Binning</label>
            <RMSlider
              min={1}
              max={10}
              value={binSize}
              onChange={(val) => {
                setBinSize(val);
              }}
            />
          </div>
        )}
        <div
          id="index-searchandfilter-analytics-show-labels"
          className="flex flex-row gap-3"
        >
          <RMCheckBox isChecked={showLabels} onChange={handleShowLabelsChange} />
          <label id="index-searchandfilter-analytics-show-labels-text">Show Labels</label>
        </div>
      </div>
      <div
        id="index-searchandfilter-analytics-display"
        className="w-full relative basis-[88%] max-h-[88%] flex flex-row"
      >
        <div
          id="index-searchandfilter-analytics-display-text"
          ref={chartRef}
          className="p-2 h-full w-full basis-full"
        >
          Analytics
        </div>
        <div
          id="index-searchandfilter-analytics-display-legends"
          className="absolute right-0 mt-4 h-[80%] w-[15%] basis-[15%] overflow-y-auto content-center"
        >
          <Legends titleColorsMap={colorsMap} onSelectionChange={onLegendSelectChanged} />
        </div>
      </div>
    </div>
  );
};

export default Analytics;
